import { DirectionProvider } from '@radix-ui/react-direction'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { AppUpdateBanner } from '@/components/AppUpdateBanner'
import { CookieConsent } from '@/components/CookieConsent'
import { MfaSetupPanel } from '@/components/mfa/MfaSetupPanel'
import { useAppVersionCheck } from '@/hooks/useAppVersionCheck'

import { getMfaStatus } from './api/auth/auth.api'
import { useCurrentUser } from './api/auth/queries/useCurrentUser'
import { useAppConfig } from './api/config/queries/useAppConfig'
//utils
import { currentAuthenticatedUser } from './api/user/userService'
import { ImpersonationBanner } from './components/ImpersonationBanner'
import { LoadingScreen } from './components/LoadingScreen'
import { MaintenanceScreen } from './components/MaintenanceScreen'
import { ToastContainer, ToastProvider } from './components/ui/toast'
import environment from './environment'
import { getPreferredLanguage } from './i18n'
import Layout from './Layout'
import { setAnalyticsUserId } from './lib/analytics'
import { claritySet, claritySetUser } from './lib/clarity'
import { captureClickIdsFromUrl } from './lib/clickIds'
import { useImpersonatedUserId } from './lib/impersonation'
import { isOfficeTranslatedPath } from './lib/officeI18n'
import { QUERY_KEYS } from './lib/queryKeys'
import NavBar from './NavBar'
import OfficeNavbar from './pages/office-side/OfficeNavbar'
import { OfficeRoutePath, RoutePath } from './RoutePath'
import { setIsAuthenticated } from './store/appSlice'

const getPostLoginRoute = (permissions?: Record<string, boolean>) => {
  if (permissions?.AffiliateManager) return OfficeRoutePath.AffiliateManager
  if (permissions?.PickupOrderDriver) return OfficeRoutePath.ScanPickupOrders
  return RoutePath.ClientAddresses
}

export default function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isReady, setIsReady] = useState(false)
  const [adminNeedsMfaSetup, setAdminNeedsMfaSetup] = useState(false)
  const { data: loggedUser } = useCurrentUser()
  const { data: appConfig, isLoading: isAppConfigLoading, error: appConfigError } = useAppConfig()
  const isMaintenance =
    !loggedUser?.permissions?.['Admin'] && (appConfig?.isMaintenance || (appConfigError as AxiosError)?.response?.status === 503)

  const location = useLocation()
  const isOfficePage = location.pathname.startsWith('/office') || location.pathname.startsWith('/all-packages-view')
  const isOfficeTranslatedPage = isOfficeTranslatedPath(location.pathname)
  const { i18n } = useTranslation()

  const impersonatedUserId = useImpersonatedUserId()
  const showImpersonationBanner =
    !!impersonatedUserId && (location.pathname.startsWith('/payment') || location.pathname.startsWith('/receipt-upload'))
  const shouldShowLoading = !isReady || isAppConfigLoading
  const { updateAvailable, refresh: refreshApp } = useAppVersionCheck()

  const preferredLang = getPreferredLanguage()
  const activeLang = i18n.language.startsWith('he') ? 'he' : 'en'
  const appDir = !isOfficePage ? 'rtl' : isOfficeTranslatedPage ? i18n.dir(preferredLang) : 'ltr'

  useEffect(() => {
    if (!isOfficePage) {
      // Client side: always Hebrew (do not overwrite preferred office locale)
      void i18n.changeLanguage('he')
      document.documentElement.lang = 'he'
      document.documentElement.dir = 'rtl'
      return
    }

    if (!isOfficeTranslatedPage) {
      // Office pages without translations: always English (do not overwrite preferred locale)
      void i18n.changeLanguage('en')
      document.documentElement.lang = 'en'
      document.documentElement.dir = 'ltr'
      return
    }

    // Office pages with translations: restore the user's remembered locale
    if (activeLang !== preferredLang) {
      void i18n.changeLanguage(preferredLang)
    }
    document.documentElement.lang = preferredLang
    document.documentElement.dir = i18n.dir(preferredLang)
  }, [isOfficePage, isOfficeTranslatedPage, activeLang, preferredLang, i18n])

  useEffect(() => {
    if (window.location.href.includes('office')) {
      const enableToolbar = document.getElementById('enable-toolbar')
      if (enableToolbar) enableToolbar.style.display = 'none'
    }
  }, [])

  useEffect(() => {
    captureClickIdsFromUrl()
  }, [])

  useEffect(() => {
    ;(async () => {
      try {
        const userData = await currentAuthenticatedUser()
        if (!userData) {
          localStorage.removeItem('cognito-tokens')
          dispatch(setIsAuthenticated(false))
          return
        }
        queryClient.setQueryData([QUERY_KEYS.LOGGED_USER], userData)
        dispatch(setIsAuthenticated(true))
        if (location.pathname == '/') navigate(getPostLoginRoute(userData?.permissions))
        else navigate(location.pathname + location.search + location.hash)
      } catch {
        dispatch(setIsAuthenticated(false))
      } finally {
        setIsReady(true)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!environment.isProd || !loggedUser?.permissions?.Admin) {
      setAdminNeedsMfaSetup(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const status = await getMfaStatus()
        if (!cancelled) setAdminNeedsMfaSetup(!status.enabled)
      } catch {
        if (!cancelled) setAdminNeedsMfaSetup(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [loggedUser?.id, loggedUser?.permissions?.Admin])

  // Set Sentry user context when user is logged in
  useEffect(() => {
    if (loggedUser?.id) {
      Sentry.setUser({
        id: loggedUser.id,
        email: loggedUser.email,
        username: loggedUser.name || loggedUser.id,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [loggedUser])

  useEffect(() => {
    if (loggedUser?.id) {
      claritySetUser({ id: loggedUser.id, email: loggedUser.email })
      setAnalyticsUserId(loggedUser.id)
    } else {
      claritySetUser(null)
      setAnalyticsUserId(null)
    }
    claritySet('environment', import.meta.env.MODE ?? 'development')
  }, [loggedUser?.id, loggedUser?.email])

  const hardRefresh = () => {
    localStorage.clear()
    navigate('/')
    window.location.reload()
  }

  return (
    <DirectionProvider dir={appDir}>
      <ToastProvider>
        <div className='h-[100dvh]'>
          {isMaintenance ? (
            <MaintenanceScreen />
          ) : shouldShowLoading ? (
            <LoadingScreen onRefresh={hardRefresh} />
          ) : adminNeedsMfaSetup ? (
            <div className='flex min-h-[100dvh] items-center justify-center bg-gray-50 p-6'>
              <div className='w-full max-w-[480px] rounded-2xl bg-white p-6 shadow-sm'>
                <MfaSetupPanel required onComplete={() => setAdminNeedsMfaSetup(false)} />
              </div>
            </div>
          ) : (
            <>
              {loggedUser?.permissions['Admin'] ||
              loggedUser?.permissions['Agent'] ||
              loggedUser?.permissions['SeniorAgent'] ||
              loggedUser?.permissions['OfficeManager'] ||
              loggedUser?.permissions['Worker'] ||
              loggedUser?.permissions['PickupOrderDriver'] ||
              loggedUser?.permissions['AffiliateManager'] ? (
                <OfficeNavbar />
              ) : (
                <NavBar />
              )}
              {showImpersonationBanner ? <ImpersonationBanner /> : null}
              <Layout />
            </>
          )}
        </div>
        <ToastContainer />
        {updateAvailable ? <AppUpdateBanner onRefresh={refreshApp} /> : null}
        <CookieConsent />
      </ToastProvider>
    </DirectionProvider>
  )
}
